"use client";

import { Input, Textarea } from "@/components/ui";
import { BASE_URL } from "@/lib/constants";
import { useAuth } from "@/providers/auth-provider";
import { useModal } from "@/providers/modal-provider";
import { Loader2Icon } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { ModalType } from "@/providers/modal-provider.types";

const GrowWithUsForm = ({ message = "" }: { message?: string }) => {
	const { user } = useAuth();
	const { closeModal } = useModal();

	const nameFromSession = user?.name || "";
	const emailFromSession = user?.email || "";

	const [loading, setLoading] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
	const [form, setForm] = useState({
		name: nameFromSession,
		email: emailFromSession,
		message: message,
	});

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setForm({
			...form,
			[name]: value,
		});

		setErrorMessage(undefined);
	};

	const handleSubmit = async () => {
		try {
			setLoading(true);

			if (!form.name.trim()) {
				setErrorMessage("Name is required");
				return;
			}
			if (!form.email.trim()) {
				setErrorMessage("Email is required");
				return;
			}

			const url = new URL(`${BASE_URL}/api/grow-with-us`);
			const body = JSON.stringify(form);

			const response = await fetch(url, { method: "POST", body });

			if (!response.ok) {
				const { message } = await response.json();

				setErrorMessage(message);
			} else {
				toast.success("Thanks for your interest, we will get back to you shortly");

				closeModal(ModalType.GrowWithUs);
			}
		} catch (error) {
			console.log("Error occured while submitting grow with us form", error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div>
			<p className="text-sm text-gray-500">
				Thank you for your interest, Please fill out the form to get in touch with our Growth &
				advertising experts.
			</p>

			{errorMessage && (
				<div className="mt-4 rounded-md border border-red-100 bg-red-50 px-4 py-2 text-xs text-red-500">
					{errorMessage}
				</div>
			)}

			<Input
				type="text"
				id="name"
				name="name"
				label="Name"
				defaultValue={nameFromSession}
				placeholder="Enter your full name"
				classNames={{ container: "mt-4 mb-4" }}
				onChange={handleOnChange}
			/>
			<Input
				type="email"
				id="email"
				name="email"
				label="Email"
				defaultValue={emailFromSession}
				placeholder="Enter your email address"
				classNames={{ container: "mb-4", input: "" }}
				onChange={handleOnChange}
			/>
			<Textarea
				name="message"
				id="message"
				label="Message"
				placeholder="Type your message"
				className="mb-4"
				defaultValue={message}
				onChange={handleOnChange}
			/>

			<button
				type="button"
				disabled={loading}
				onClick={handleSubmit}
				className="block w-full rounded-md bg-brand-primary py-3 text-center text-white disabled:cursor-not-allowed disabled:opacity-60"
			>
				{loading ? <Loader2Icon className="mx-auto h-5 w-5 animate-spin text-white" /> : "Submit"}
			</button>
		</div>
	);
};

export default GrowWithUsForm;
